import React,{ useEffect, useRef, useState } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCHookToast, RCCIcon, RCCInput, RCCRefModalProps, RCCServicesApi, RCCTooltip } from "react-common-components";
import { useForm } from "react-hook-form";
import AppIcons from "src/components/AppIcons";
import Audit from "src/components/Audit";
import DeleteModal from "src/components/DeleteModal";
import FiltersContainer from "src/components/FiltersContainer";
import Modal from "src/components/Modal";
import ModalSide from "src/components/Modal/styles";
import ModalReorder from "src/components/ModalReorder";
import { IReorderRef } from "src/components/ModalReorder/interface";
import PageTitle from "src/components/PageTitle";
import Title from "src/components/Title";
import { useAuth } from "src/context/Auth/context";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import { v4 } from "uuid";
import { IPatba001 } from "./interface";
import ParameterGroup from "./parameterGroup";
import {deleteSchema,AddOrChangeSchema} from "./schemas";

const ParametersGroup : React.FC  = () => {
	const { translate} = useTranslation();
	const { addToast} = RCCHookToast();
	const {superUser} = useAuth();
	const { setLoading } = useLoaderBackdrop();
	const controllerUrl = "Parameters/";

	const [showFilters, setShowFilters] = useState(false);
	const [groups, setGroups] = useState<IPatba001[]>();    
	const [filters, setFilters] = useState<{ filter: string}>({ filter: ""});
	const {
		register,
		handleSubmit,
		reset
	}= useForm();

	const modalReorderRef = useRef<IReorderRef>(null);

	const modalDelete = useRef<RCCRefModalProps>(null);
	const [deletedGroup, setDeletedGroup] = useState<{id:number,title:string}>({id:0,title:""});
	const { 
		handleSubmit: handleSubmitDelete, 
		register: registerDelete, 
		reset : resetDelete, 
		setError : setErrorDelete, 
		formState: { errors : errorsDelete }, 
		clearErrors: clearErrorsDelete 
	} = useForm();
	const submitDelete = async (data: any) => {
		try{
			clearErrorsDelete();
			modalDelete.current?.setLoadingButton();
			
			await deleteSchema(translate).validate({...data, "paa001GrupoConfirmation": deletedGroup.title},{
				abortEarly: false
			});
			
			await RCCServicesApi.deleteRequest({
				method: `${controllerUrl}DeleteGroup`,
				urlParameters: {
					id: deletedGroup.id
				}
			});

			addToast({
				color: "green",
				description: translate("Grupo de parâmetros excluído com sucesso" ),
				title: translate("Sucesso")
			});

			modalDelete.current?.resetLoadingButton();
			modalDelete.current?.closeModal();
			setGroups((prevState) => prevState?.filter((x) => x.paa001Id !== deletedGroup.id));

		}catch(err){

			modalDelete.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorDelete))
				return;

			standardErrorMessages(err, translate("Erro ao excluir grupo de prâmetros"), addToast);
		}
	};
	const deleteGroupFunction = (id:number,title:string) => {
		setDeletedGroup({id,title});
		resetDelete();
		modalDelete.current?.openModal();
	};

	const [isAdd, setIsAdd] = useState(false);
	const [selectedgroup,setSelectedgroup] = useState<IPatba001>();
	const modalAddOrChange = useRef<RCCRefModalProps>(null);
	const { 
		handleSubmit : handleSubmitAddOrChange, 
		register : registerAddOrChange, 
		reset : resetAddOrChange, 
		setError : setErrorAddOrChange, 
		formState : { errors : errorsAddOrChange }, 
		clearErrors : clearErrorsAddOrChange 
	} = useForm();

	const updateGroupFunction = (group:IPatba001) => {
		resetAddOrChange();
		setSelectedgroup(group);
		setIsAdd(false);
		modalAddOrChange.current?.openModal();
	};

	const AddGroupFunction = () => {
		resetAddOrChange();
		setSelectedgroup(undefined);
		setIsAdd(true);
		modalAddOrChange.current?.openModal();
	};

	
	const RequestGroups = async() =>
	{
		try {
			setLoading(true);
			const dataController = await RCCServicesApi.getRequest({
				method: `${controllerUrl}List`,
				urlParameters:{...filters}	
			});

			setGroups(dataController.data);
            
			setLoading(false);

		} catch (error) {
			standardErrorMessages(error, translate("Erro ao buscar grupos de parâmetros"), addToast);
			setLoading(false);
		}
	};
	
	const submitGroups = async(data: any) => {
		try{
			clearErrorsAddOrChange();
			modalAddOrChange.current?.setLoadingButton();
			
			await AddOrChangeSchema(translate).validate({...data}, {
				abortEarly: false
			});

			if(isAdd){
				await RCCServicesApi.postRequest({
					method: `${controllerUrl}InsertGroup`,
					body: {...data}
				});
			}else{
				await RCCServicesApi.putRequest({
					method: `${controllerUrl}UpdateGroup`,
					body: {...data}
				});
			}
	
			addToast({
				color: "green",
				description: isAdd? translate("Grupo de parâmetros adicionado com sucesso") : translate("Grupo de parâmetros alterado com sucesso"),
				title: translate("Sucesso")
			});

			RequestGroups();
			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();
	
		}catch(err){
			modalAddOrChange.current?.resetLoadingButton();
			if(getValidationErrors(err,setErrorAddOrChange)){
				return;
			}
	
			standardErrorMessages(err, isAdd ? translate("Erro ao adicionar grupo de parâmetros") : translate("Erro ao alterar grupo de parâmetros"), addToast);
		}
	};
	useEffect(() => {
		RequestGroups();
	}, [filters]);

	return (	
		<RCCGrid
			container
			item
			direction="column"
		>
			{/* Header -> Results + Buttons */}
			<PageTitle
				title={translate("Grupos de parâmetros")}
				icon={() => <AppIcons name="configs" />}
			>
				{superUser && 
				<>
					<RCCTooltip
                    	text={translate("Novo grupo")}
                    	color="purple"
					>
                    	<RCCButton
                    		icon={{name: "plus"}}
                    		color="purple"
                    		layout="isRounded"
                    		onClick={AddGroupFunction}
                    		size="md"
                    	/>
					</RCCTooltip>
					<RCCTooltip
						text={translate("Reordenar grupos")}
						color='grey'
					>
						<RCCButton
							color='grey'
							layout="isRounded"
							size="md"
							onClick={() => {modalReorderRef.current?.openModalReorder();}}
						>
							<RCCGrid
								styleInline="transform: rotate(90deg);"
							>
								<RCCIcon
									name= "exchange" 
									color="#FFF" 
									size={16}
								/>
							</RCCGrid>		
						</RCCButton>
					</RCCTooltip>		
				</>
				}
				<RCCTooltip
					text={translate("Filtros")}
					color={showFilters || filters.filter ? "blue" : "grey"}
				>
					<RCCButton
						icon={{name: "filter", color: "#FFF", size: 16}}
						color={showFilters || showFilters || filters.filter ? "blue" : "grey"}
						layout="isRounded"
						size="md"
						onClick={() => setShowFilters((prevState) => !prevState)}
					/>
				</RCCTooltip>
			</PageTitle>

			{/* Filters */}
			<FiltersContainer
				onSubmitFilters={handleSubmit((data) => setFilters({filter:data.filter}))}
				resetFilters={
					() => reset({filter: ""})
				}
				showFilters={showFilters}
			>
				<RCCGrid
					item
					xs={12}
					sm={8}
					md={6}
					lg={6}
				>
					<RCCInput
						name="filter" 
						label={translate("Pesquisar")}
						register={register}
					/>
				</RCCGrid>
			</FiltersContainer>
            
			<RCCGrid container>
				{!groups || groups.length === 0 ? 
					<RCCAlert 
						title={translate("Atenção")}	
						description={translate("Não foram grupos resultados para esta pesquisa")}
					/>
					:
					groups?.map((group) => (
						<ParameterGroup
							key={v4()}
							group={group}
							filters={filters}
							deleteAction={deleteGroupFunction}
							updateAction={updateGroupFunction}
						/>
					))}
			</RCCGrid>
			
			<DeleteModal
				ref={modalDelete}
				title= {translate("Exclusão de grupo")}
				description= {`${translate("Deseja realmente excluir o grupo")}: ${deletedGroup.title}?`}
				onSubmit = {handleSubmitDelete(submitDelete)}
				height="250px"
			>
				<RCCGrid
					container
					item
					xs={10}
				>
					<RCCInput 
						register={registerDelete}
						name="paa001Grupo"
						label={translate("Digite o nome do grupo para excluí-lo")}
						maxLength={200}
						error={errorsDelete.paa001Grupo}
						required
					/>
				</RCCGrid>
			</DeleteModal>

			<ModalReorder 
				ref={modalReorderRef}
				modalName={translate("Reordenar grupos de parâmetros")}
				names={{
					nameForId: "paa001Id",
					nameForOrder: "paa001Ordem",
					nameForTitle: "paa001Grupo"
				}}
				routeToList={`${controllerUrl}List`}
				routeToSave={`${controllerUrl}ReorderGroups`}
				onCallbackSave={() => RequestGroups()}
			/>

			<Modal
				ref={modalAddOrChange}
				header={{
					title: isAdd ? translate("Adicionar grupo de parâmetro") : translate("Alterar grupo de parâmetro"),
					icon: {
						name: "infoCircle"
					}
				}}
				onSubmit={handleSubmitAddOrChange(submitGroups)}
				styleInline={ModalSide}
			>
				<RCCGrid
					container
					alignItems="center"
					justifyContent="center"
				>
					<RCCGrid
						container
						item
						alignItems="center"
						justifyContent="space-between"
						xs={12}
						sm={10}
					>
						<Title color="purple" size="md">
						    	{translate("Dados Gerais")}
						</Title>
						<Audit data={selectedgroup||{}} type="pop"/>
						<hr />
					</RCCGrid>
					<RCCGrid item xs={12} sm={10}>
						<input 
							{...registerAddOrChange("paa001Id")} 
							name="paa001Id"
							value={selectedgroup?.paa001Id||0}
							type="hidden"
						/>
						<RCCInput 
							register={registerAddOrChange}
							name="paa001Grupo"
							label={translate("Grupo")}
							error={errorsAddOrChange.paa001Grupo}
							defaultValue={selectedgroup?.paa001Grupo}
							required
							maxLength={15}
						/>
					</RCCGrid>
					<RCCGrid item xs={12} sm={10}>
						<RCCInput 
							register={registerAddOrChange}
							name="paa001Descricao"
							label={translate("Descrição")}
							error={errorsAddOrChange.paa001Descricao}
							defaultValue={selectedgroup?.paa001Descricao}
							maxLength={200}
							required
						/>
					</RCCGrid>
				</RCCGrid>
			</Modal>
		</RCCGrid>
	);

};

export default ParametersGroup;
